import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";

const CHIP_HEIGHT_PX = 24;

/**
 * Envolve chips de filtro e limita a exibição a N linhas quando recolhido.
 */
export default function FilterChipsCollapsible({
  children,
  maxLines = 2,
  gap = 0.5,
  seeMoreLabel = "Ver mais",
  seeLessLabel = "Ver menos",
  sx,
}) {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const containerRef = useRef(null);
  const rowGapPx = gap * 8;
  const maxHeight =
    maxLines * CHIP_HEIGHT_PX + Math.max(0, maxLines - 1) * rowGapPx;

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setHasOverflow(el.scrollHeight > maxHeight + 1);
  }, [maxHeight]);

  useEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, children]);

  useEffect(() => {
    if (!hasOverflow) setExpanded(false);
  }, [hasOverflow]);

  return (
    <Box sx={sx}>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap,
          overflow: expanded ? "visible" : "hidden",
          maxHeight: expanded ? "none" : maxHeight,
        }}
      >
        {children}
      </Box>
      {hasOverflow && (
        <Button
          size="small"
          color="primary"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            mt: 0.25,
            ml: -0.5,
            px: 0.5,
            minWidth: 0,
            textTransform: "none",
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {expanded ? seeLessLabel : seeMoreLabel}
        </Button>
      )}
    </Box>
  );
}
